import { Link } from "@tanstack/react-router"
import { IconGavel } from "@tabler/icons-react"
import { Container, Grid, Group, Text, Divider, Box, useMantineColorScheme } from "@mantine/core"
import { ShareButtons } from "@/components/ui/ShareButtons"

export function Footer() {
  const currentYear = new Date().getFullYear()
  const { colorScheme } = useMantineColorScheme()

  const footerLinks = [
    {
      title: "Resources",
      links: [
        { href: "/documents", label: "Court Documents" },
        { href: "/timeline", label: "Case Timeline" },
        { href: "/news", label: "Latest News" },
      ],
    },
    {
      title: "About",
      links: [
        { href: "/about", label: "Mission" },
        { href: "/methodology", label: "Methodology" },
        { href: "/contact", label: "Contact" },
        { href: "/privacy", label: "Privacy Policy" },
      ],
    },
    {
      title: "Other Perspectives",
      links: [
        { href: "/faith", label: "Faith & Justice" },
        { href: "/conservative", label: "Conservative View" },
        { href: "/parents", label: "Parents & Families" },
        { href: "/reform", label: "Justice Reform" },
      ],
    },
  ]

  return (
    <footer
      style={{
        background:
          colorScheme === "dark"
            ? "linear-gradient(180deg, rgba(16, 16, 16, 0.95) 0%, rgba(10, 10, 10, 0.95) 100%)"
            : "linear-gradient(180deg, rgba(249, 250, 251, 0.95) 0%, rgba(243, 244, 246, 0.95) 100%)",
        borderTop: colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
      }}
    >
      <Container size="lg" py="xl">
        <Grid>
          {/* Brand & Mission */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Group mb="lg">
              <Box
                style={{
                  background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  borderRadius: "12px",
                  padding: "8px",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                }}
              >
                <IconGavel size={20} color="white" />
              </Box>
              <div>
                <Text size="lg" fw={700} style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
                  RTEFN
                </Text>
                <Text size="sm" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.6)" : "#6b7280" }}>
                  Release The Epstein Files Now
                </Text>
              </div>
            </Group>

            <Text
              size="sm"
              mb="lg"
              style={{
                color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#6b7280",
                lineHeight: 1.6,
                maxWidth: "300px",
              }}
            >
              Advocating for transparency and justice through the public release of Jeffrey Epstein case files.
            </Text>

            <div>
              <Text
                size="xs"
                fw={600}
                mb="xs"
                style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#374151" }}
              >
                Share This Site
              </Text>
              <ShareButtons size="lg" variant="subtle" />
            </div>
          </Grid.Col>

          {/* Navigation Links */}
          <Grid.Col span={{ base: 12, sm: 6, md: 2.5 }}>
            <Text size="sm" fw={600} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
              Resources
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {footerLinks[0].links.map((link) => (
                <Text
                  key={link.href}
                  component={Link}
                  to={link.href}
                  size="sm"
                  style={{
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#6b7280",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: colorScheme === "dark" ? "#ffffff" : "#374151",
                    },
                  }}
                >
                  {link.label}
                </Text>
              ))}
            </div>
          </Grid.Col>

          {/* About Links */}
          <Grid.Col span={{ base: 12, sm: 6, md: 2.5 }}>
            <Text size="sm" fw={600} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
              About
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {footerLinks[1].links.map((link) => (
                <Text
                  key={link.href}
                  component={Link}
                  to={link.href}
                  size="sm"
                  style={{
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#6b7280",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: colorScheme === "dark" ? "#ffffff" : "#374151",
                    },
                  }}
                >
                  {link.label}
                </Text>
              ))}
            </div>
          </Grid.Col>

          {/* Other Perspectives */}
          <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
            <Text size="sm" fw={600} mb="md" style={{ color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a" }}>
              Other Perspectives
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {footerLinks[2].links.map((link) => (
                <Text
                  key={link.href}
                  component={Link}
                  to={link.href}
                  size="sm"
                  style={{
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.7)" : "#6b7280",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: colorScheme === "dark" ? "#ffffff" : "#374151",
                    },
                  }}
                >
                  {link.label}
                </Text>
              ))}
            </div>
          </Grid.Col>
        </Grid>

        <Divider my="lg" color={colorScheme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"} />

        {/* Bottom Bar */}
        <Group justify="space-between" align="center">
          <Text size="xs" style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.5)" : "#9ca3af" }}>
            © {currentYear} RTEFN. All rights reserved.
          </Text>
          <Text size="xs" fw={500} style={{ color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.5)" : "#9ca3af" }}>
            Transparency • Justice • Accountability
          </Text>
        </Group>
      </Container>
    </footer>
  )
}
